// Per-player superlative engine for Season Wrapped.
//
// Goal (Dan's call): EVERY kid gets a card IF we can find them an honest, positive
// hook — otherwise they fold into the team story rather than get an empty or
// below-teammates card. Never comparative, never negative.
//
// We walk a ranked list of positive angles and let each player claim the best one
// still available to them. "Leader" / "clutch" are scarce (one team can only have
// one batting-average leader); softer angles (grinder, personal-best) are the
// safety net that gives almost any active player an honest highlight.

import type { AtBatDoc, WrappedSuperlative } from '../../types/dugout';
import type { BiggestMomentResult } from './biggest-moment';

interface SuperlativeInput {
  /** Finalized per-player stats map from buildSeasonStats (keyed by player id). */
  stats: Record<string, any>;
  players: any[];
  games: any[];
  atBats: AtBatDoc[];
  moments: BiggestMomentResult;
}

// Minimum sample gates so a leader card is fair (not crowned off 2 PA).
const MIN_PA_FOR_RATE = 8;
const MIN_IP_FOR_ERA = 4;

const fmtAvg = (n: number) => (n != null ? n.toFixed(3).replace(/^0/, '') : '—');
const fmtRate = (n: number) => (n != null ? n.toFixed(3).replace(/^0/, '') : '—');
const fmtERA = (n: number) => (n != null ? n.toFixed(2) : '—');
const fmtIP = (n: number) => {
  // youth IP is often whole/third innings; show one decimal when needed
  return Number.isInteger(n) ? String(n) : n.toFixed(1);
};

/** Pick the player who leads a numeric accessor, respecting a sample gate. */
function leaderBy(
  stats: Record<string, any>,
  value: (s: any) => number | null | undefined,
  gate: (s: any) => boolean,
): { id: string; s: any; v: number } | null {
  let best: { id: string; s: any; v: number } | null = null;
  for (const [id, s] of Object.entries(stats)) {
    if (!gate(s)) continue;
    const v = value(s);
    if (v == null || !isFinite(v)) continue;
    if (!best || v > best.v) best = { id, s, v };
  }
  return best;
}

/** Most-improved: split a player's games into first-half vs second-half and
 *  compare a rate. Returns the delta and the late value when meaningful. */
function improvementDelta(
  playerId: string,
  games: any[],
  metric: 'ops' | 'avg' | 'obp',
): { delta: number; early: number; late: number } | null {
  const withStat = games
    .filter(g => g.status === 'completed' && g.stats?.[playerId])
    .map(g => ({ date: g.date || '', s: g.stats[playerId] }))
    .filter(x => (x.s.pa || 0) > 0 || (x.s.ab || 0) > 0)
    .sort((a, b) => new Date(a.date || 0).getTime() - new Date(b.date || 0).getTime());

  if (withStat.length < 6) return null; // need a real arc

  const mid = Math.floor(withStat.length / 2);
  const half = (arr: typeof withStat) => {
    let pa = 0, ab = 0, h = 0, bb = 0, wOps = 0, sumOps = 0;
    arr.forEach(({ s }) => {
      pa += s.pa || 0; ab += s.ab || 0; h += s.h || 0; bb += s.bb || 0;
      if (s.ops != null) { sumOps += s.ops * (s.pa || 1); wOps += s.pa || 1; }
    });
    if (metric === 'ops') return wOps > 0 ? sumOps / wOps : (ab > 0 ? h / ab : 0);
    if (metric === 'avg') return ab > 0 ? h / ab : 0;
    return pa > 0 ? (h + bb) / pa : 0; // obp
  };
  const early = half(withStat.slice(0, mid));
  const late = half(withStat.slice(mid));
  return { delta: late - early, early, late };
}

/** A player's single best game (for the personal-best fallback). */
function bestGame(playerId: string, games: any[]): { hits: number; rbi: number; date: string } | null {
  let best: { hits: number; rbi: number; date: string } | null = null;
  for (const g of games) {
    const s = g.stats?.[playerId];
    if (!s) continue;
    const hits = s.h || 0;
    const rbi = s.rbi || 0;
    const score = hits * 2 + rbi;
    if (hits === 0 && rbi === 0) continue;
    if (!best || score > best.hits * 2 + best.rbi) best = { hits, rbi, date: g.date || '' };
  }
  return best;
}

/** Season game-ball count: prefer the archived season field, else count live recipients. */
function gameBalls(playerId: string, player: any, games: any[]): number {
  if (typeof player?.seasonGameBalls === 'number' && player.seasonGameBalls > 0) return player.seasonGameBalls;
  if (player?.gameBallLog) {
    const sum = Object.values(player.gameBallLog as Record<string, number>).reduce((a, b) => a + (b || 0), 0);
    if (sum > 0) return sum;
  }
  return games.reduce((n, g) => n + ((g.gameBallRecipients || []).includes(playerId) ? 1 : 0), 0);
}

/**
 * Assign each player their best available positive superlative. Players with no
 * honest hook are omitted (they fold into the team story). One scarce award per
 * category (e.g. a single batting-average "leader") so cards don't all say the
 * same thing.
 */
export function assignPlayerSuperlatives(input: SuperlativeInput): WrappedSuperlative[] {
  const { stats, players, games, atBats, moments } = input;
  const claimed = new Set<string>();          // player ids that already have a card
  const out: WrappedSuperlative[] = [];

  const nameOf = (id: string) => stats[id]?.player?.name || 'Player';
  const numOf = (id: string) => stats[id]?.player?.number || '';

  const add = (id: string, sup: Omit<WrappedSuperlative, 'playerId' | 'playerName' | 'number' | 'prose'>) => {
    if (claimed.has(id)) return;
    claimed.add(id);
    out.push({ playerId: id, playerName: nameOf(id), number: numOf(id), prose: '', ...sup });
  };

  // ── 1. Clutch player (scarcest, most emotional) ────────────────────────────
  if (moments.clutchPlayer?.playerId) {
    add(moments.clutchPlayer.playerId, {
      angle: 'clutch',
      title: 'Mr. Clutch',
      statLine: `${moments.clutchPlayer.moments} big moments`,
    });
  }

  // ── 2. Statistical leaders (one per category, best categories first) ───────
  const opsLeader = leaderBy(stats, s => s.batting.ops, s => (s.batting.pa || 0) >= MIN_PA_FOR_RATE);
  if (opsLeader && opsLeader.v > 0) {
    add(opsLeader.id, { angle: 'leader', title: 'Team OPS Leader', statLine: `${fmtRate(opsLeader.v)} OPS` });
  }
  const avgLeader = leaderBy(stats, s => s.batting.avg, s => (s.batting.pa || 0) >= MIN_PA_FOR_RATE);
  if (avgLeader && avgLeader.v > 0) {
    add(avgLeader.id, { angle: 'leader', title: 'Best Average', statLine: `${fmtAvg(avgLeader.v)} avg` });
  }
  const rbiLeader = leaderBy(stats, s => s.batting.rbi, s => (s.batting.rbi || 0) > 0);
  if (rbiLeader && rbiLeader.v > 0) {
    add(rbiLeader.id, { angle: 'leader', title: 'RBI Machine', statLine: `${rbiLeader.v} RBI` });
  }
  const hitsLeader = leaderBy(stats, s => s.batting.h, s => (s.batting.h || 0) > 0);
  if (hitsLeader && hitsLeader.v > 0) {
    add(hitsLeader.id, { angle: 'leader', title: 'Hit King', statLine: `${hitsLeader.v} hits` });
  }
  const runsLeader = leaderBy(stats, s => s.batting.r, s => (s.batting.r || 0) > 0);
  if (runsLeader && runsLeader.v > 0) {
    add(runsLeader.id, { angle: 'leader', title: 'Run Scorer', statLine: `${runsLeader.v} runs` });
  }
  const sbLeader = leaderBy(stats, s => s.batting.sb, s => (s.batting.sb || 0) > 0);
  if (sbLeader && sbLeader.v > 0) {
    add(sbLeader.id, { angle: 'leader', title: 'Base Stealer', statLine: `${sbLeader.v} SB` });
  }
  // Pitching leaders
  const kLeader = leaderBy(stats, s => s.pitching.so, s => (s.pitching.so || 0) > 0);
  if (kLeader && kLeader.v > 0) {
    add(kLeader.id, { angle: 'leader', title: 'Strikeout Artist', statLine: `${kLeader.v} K` });
  }
  const eraLeader = leaderBy(
    stats,
    s => (s.pitching.era != null ? -s.pitching.era : null), // lowest ERA = leader
    s => (s.pitching.ip || 0) >= MIN_IP_FOR_ERA,
  );
  if (eraLeader) {
    add(eraLeader.id, { angle: 'leader', title: 'Ace', statLine: `${fmtERA(-eraLeader.v)} ERA` });
  }

  // ── 3. Most improved (the late-bloomer's home) ─────────────────────────────
  let bestImp: { id: string; delta: number; late: number } | null = null;
  for (const p of players) {
    if (claimed.has(p.id)) continue;
    const imp = improvementDelta(p.id, games, 'ops');
    if (!imp || imp.delta < 0.08) continue; // meaningful jump only
    if (!bestImp || imp.delta > bestImp.delta) bestImp = { id: p.id, delta: imp.delta, late: imp.late };
  }
  if (bestImp) {
    add(bestImp.id, {
      angle: 'most_improved',
      title: 'Most Improved',
      statLine: `+${Math.round(bestImp.delta * 1000)} pts OPS`,
    });
  }

  // ── 4. Workhorse (most innings pitched) ────────────────────────────────────
  const ipLeader = leaderBy(stats, s => s.pitching.ip, s => (s.pitching.ip || 0) > 0);
  if (ipLeader && ipLeader.v > 0 && !claimed.has(ipLeader.id)) {
    add(ipLeader.id, { angle: 'workhorse', title: 'The Workhorse', statLine: `${fmtIP(ipLeader.v)} IP` });
  }

  // ── 5. Grinder (most walks — plate discipline) ─────────────────────────────
  const bbLeader = leaderBy(stats, s => s.batting.bb, s => (s.batting.bb || 0) >= 3);
  if (bbLeader && bbLeader.v > 0 && !claimed.has(bbLeader.id)) {
    add(bbLeader.id, { angle: 'grinder', title: 'The Grinder', statLine: `${bbLeader.v} walks drawn` });
  }

  // ── 6 & 7. Glove flash / most game balls ───────────────────────────────────
  let topGB: { id: string; n: number } | null = null;
  for (const p of players) {
    if (claimed.has(p.id)) continue;
    const n = gameBalls(p.id, p, games);
    if (n <= 0) continue;
    if (!topGB || n > topGB.n) topGB = { id: p.id, n };
  }
  if (topGB) {
    add(topGB.id, {
      angle: 'game_balls',
      title: 'Game Ball Magnet',
      statLine: `${topGB.n} game ball${topGB.n === 1 ? '' : 's'}`,
    });
  }

  // ── 8. Personal-best game (the catch-all so almost anyone gets a real moment) ─
  for (const p of players) {
    if (claimed.has(p.id)) continue;
    const bg = bestGame(p.id, games);
    if (!bg) continue;
    // Only worth a card if it was actually a nice game.
    if (bg.hits >= 2 || bg.rbi >= 2) {
      const parts: string[] = [];
      if (bg.hits >= 1) parts.push(`${bg.hits}-hit`);
      if (bg.rbi >= 1) parts.push(`${bg.rbi} RBI`);
      add(p.id, {
        angle: 'personal_best',
        title: 'Signature Game',
        statLine: parts.join(', ') + ' game',
      });
    }
  }

  return out;
}
